import BigNumber from "bignumber.js";
import { ethers } from "ethers";
import { useCallback } from "react";
import { useAccount, useBalance } from "wagmi";
import { useSendTransaction } from "wagmi";
import { useWriteContract } from "wagmi";
import type { UseBalanceReturnType } from "wagmi";
import { ETH_AMOUNT, KOLZ_TOKEN_ADDRESS, TREASURY_ADDRESS } from "../../constants";
import { KOLZ_TOKEN_ABI } from "../contracts";
import { PAYMENT_ERROR_INSUFFICIENT_BALANCE } from "../errors";
import usePrices from "./usePrices";

export const checkSufficientBalance = (tokenBalance: UseBalanceReturnType, requiredAmount: bigint) => {
  if (!tokenBalance || !tokenBalance.data) throw new Error("Error retrieving token balance");
  const tokenBalanceBigInt = new BigNumber(tokenBalance.data.value.toString());
  return tokenBalanceBigInt.gte(requiredAmount.toString());
};

export default function useHandlePayments() {
  const { sendTransactionAsync } = useSendTransaction();
  const { writeContractAsync } = useWriteContract();
  const { address } = useAccount();
  const tokenBalance = useBalance({
    address,
    token: KOLZ_TOKEN_ADDRESS,
  });
  const { halfEthInKolz } = usePrices();

  const handlePaymentETH = useCallback(async () => {
    const parsedAmount = ethers.parseEther(ETH_AMOUNT);
    const tx = await sendTransactionAsync({
      to: TREASURY_ADDRESS,
      value: parsedAmount,
    });
    return tx;
  }, [sendTransactionAsync]);

  const handlePaymentKOLZ = useCallback(async () => {
    const parsedAmount = ethers.parseEther(halfEthInKolz.toString());

    if (!checkSufficientBalance(tokenBalance, parsedAmount)) {
      throw new Error(PAYMENT_ERROR_INSUFFICIENT_BALANCE);
    }

    const tx = await writeContractAsync({
      address: KOLZ_TOKEN_ADDRESS,
      abi: KOLZ_TOKEN_ABI,
      functionName: "transfer",
      args: [TREASURY_ADDRESS, parsedAmount],
    });

    return tx;
  }, [tokenBalance, writeContractAsync, halfEthInKolz]);

  return { handlePaymentETH, handlePaymentKOLZ };
}
